import { apiPost, apiGet, apiPut } from './apiUtils';

const authService = {
  /**
   * User login
   */
  login: async ({ email, password }) => {
    try {
      const fcmToken = "etTQzqJTRZu9gBbe2om6Mc:APA91bG1BQGGQ_uRryVzKAG_JLOqjtNA08getuLDkAXOJuBtO1AMzbhwCT-_P1LJFIl1TzD8YqEyKC9tFpglw_egEJRE6RQDOmon4OspnOIpta02yp7kZuw";
      const fcm_platform = "android";

      const payload = { email, password, fcmToken, fcm_platform };
      console.log('Login payload:', payload);
      const result = await apiPost('/api/auth/login', payload);
      console.log('Raw login result:', result);

      // Store token and user data if login is successful (matching mobile app logic)
      if (result.success && result.data) {
        console.log('Login successful, analyzing response structure...');
        console.log('Raw result.data:', result.data);

        // Handle response structure differences between direct API and apiUtils transformation
        // apiUtils wraps response in { success, data: data.data || data, message }
        // So we need to check if result.data has token directly or if it's nested
        const loginData = result.data.token ? result.data : result.data;

        console.log('Processed loginData:', loginData);
        console.log('Token:', loginData.token);
        console.log('User data:', loginData.user);
        console.log('User role:', loginData.user?.role);
        console.log('Effective role:', loginData.effective_role);

        if (loginData.token) {
          localStorage.setItem('authToken', loginData.token);

          if (loginData.refreshToken) {
            localStorage.setItem('refreshToken', loginData.refreshToken);
          }

          if (loginData.user) {
            localStorage.setItem('user', JSON.stringify(loginData.user));
          }

          // Save userRole using effective_role logic (like mobile app)
          // For sub-users, use effective_role (parent's role) for correct routing
          const effectiveRole = loginData.effective_role || loginData.user?.role;
          if (effectiveRole) {
            localStorage.setItem('userRole', effectiveRole);
            console.log('Saved userRole to localStorage:', effectiveRole);
          }

          // Store sub-user context if applicable (matching mobile app)
          if (loginData.is_sub_user) {
            localStorage.setItem('isSubUser', 'true');
            localStorage.setItem('actualRole', loginData.user?.role);
            localStorage.setItem('actualUserId', loginData.user?.id);
            localStorage.setItem('actualUserCity', loginData.user?.profile?.city || '');
            localStorage.setItem('parentUserId', loginData.parent_user_id);
            const empName = loginData.user?.profile?.full_name || loginData.user?.profile?.first_name || loginData.user?.email || '';
            localStorage.setItem('actualUserName', empName);
          } else {
            // Clear sub-user data if not a sub-user
            localStorage.removeItem('isSubUser');
            localStorage.removeItem('actualRole');
            localStorage.removeItem('actualUserId');
            localStorage.removeItem('actualUserCity');
            localStorage.removeItem('parentUserId');
            localStorage.removeItem('actualUserName');
          }

          // Cache subscription data from login response (matching mobile app)
          if (loginData.subscription) {
            localStorage.setItem('@rebax_subscription', JSON.stringify(loginData.subscription));
          }
        }
      }

      return result;
    } catch (error) {
      console.error('Login Error:', {
        message: error.message,
        stack: error.stack,
        type: error.type,
        code: error.code,
        name: error.name
      });

      // Detailed error handling
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Login failed';

      return {
        success: false,
        message: errorMessage,
        error: error.message,
        details: {
          status: error.response?.status,
          data: error.response?.data
        }
      };
    }
  },

  /**
   * Admin login
   */
  adminLogin: async ({ email, password }) => {
    try {
      const fcmToken = "etTQzqJTRZu9gBbe2om6Mc:APA91bG1BQGGQ_uRryVzKAG_JLOqjtNA08getuLDkAXOJuBtO1AMzbhwCT-_P1LJFIl1TzD8YqEyKC9tFpglw_egEJRE6RQDOmon4OspnOIpta02yp7kZuw";
      const fcm_platform = "android";

      const payload = { email, password, fcmToken, fcm_platform };
      const result = await apiPost('/api/auth/admin/login', payload);

      // Store token if login is successful
      if (result.success && result.data?.token) {
        localStorage.setItem('authToken', result.data.token);
        if (result.data.refreshToken) localStorage.setItem('refreshToken', result.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(result.data.user));
        localStorage.setItem('userRole', result.data.user.role);
      }

      return result;
    } catch (error) {
      console.error('Admin Login Error:', error);
      return {
        success: false,
        message: error.message || 'Admin login failed',
        error: error.message
      };
    }
  }
};

/**
 * Get the current logged-in user from localStorage
 * @returns {Object|null} Current user object or null if not logged in
 */
authService.getCurrentUser = () => {
  try {
    const userString = localStorage.getItem('user');
    return userString ? JSON.parse(userString) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Check if the user is authenticated
 * @returns {boolean} True if user is logged in, false otherwise
 */
authService.isAuthenticated = () => {
  const token = localStorage.getItem('authToken');
  const user = localStorage.getItem('user');

  // Check if token exists and is not expired
  if (!token) {
    console.warn('🚫 No authentication token found');
    return false;
  }

  try {
    // Detailed token validation
    const [header, payload, signature] = token.split('.');

    if (!header || !payload || !signature) {
      console.warn('🚨 Invalid token format');
      return false;
    }

    const decoded = JSON.parse(atob(payload));
    const currentTime = Math.floor(Date.now() / 1000);

    console.group('🕰️ Token Authentication Check');
    console.log('Token Issued At:', new Date(decoded.iat * 1000).toISOString());
    console.log('Token Expires At:', new Date(decoded.exp * 1000).toISOString());
    console.log('Current Time:', new Date().toISOString());
    console.log('Time Remaining:', (decoded.exp - currentTime), 'seconds');
    console.groupEnd();

    const isValidToken = decoded.exp > currentTime;

    if (!isValidToken) {
      console.warn('🚨 Token has expired');
      // Optionally trigger auto-logout or token refresh
      this.logout();
    }

    return isValidToken;
  } catch (error) {
    console.error('🚨 Token Validation Error:', error);
    // Fallback to simple token check
    return !!token && !!user;
  }
};

/**
 * Update user profile
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} Update result
 */
authService.updateProfile = async (profileData) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Additional debugging information
    console.log('📡 Profile Update Request:', {
      endpoint: '/api/auth/profile',
      method: 'PUT',
      token: token ? `${token.substring(0, 10)}...` : 'NO TOKEN',
      payload: Object.keys(profileData)
    });

    // Use PUT method to match mobile app
    const result = await apiPut('/api/auth/profile', profileData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    // Comprehensive logging
    console.log('📥 Profile Update Response:', {
      success: result.success,
      data: result.data,
      message: result.message
    });

    if (result.success) {
      // Update local storage user data
      const userString = localStorage.getItem('user');
      if (userString) {
        const userData = JSON.parse(userString);
        const updatedUserData = {
          ...userData,
          ...profileData,
          first_name: profileData.first_name || userData.first_name,
          last_name: profileData.last_name || userData.last_name,
          full_name: profileData.full_name || userData.full_name,
          rep_phone: profileData.rep_phone || userData.rep_phone,
          email: profileData.email || userData.email,
          city: profileData.city || userData.city,
          state: profileData.state || userData.state,
          address: profileData.address || userData.address,
          pincode: profileData.pincode || userData.pincode,
          lead_provide_method: profileData.lead_provide_method || userData.lead_provide_method
        };
        localStorage.setItem('user', JSON.stringify(updatedUserData));
      }
    }

    return {
      success: result.success,
      data: result.data,
      message: result.message || (result.success ? 'Profile updated successfully' : 'Profile update failed')
    };
  } catch (error) {
    console.error('🚨 Critical Profile Update Error:', {
      message: error.message,
      stack: error.stack,
      type: error.type,
      code: error.code,
      name: error.name,
      details: error.response
    });

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update profile';

    return {
      success: false,
      message: errorMessage,
      error: error.message,
      details: {
        status: error.response?.status,
        data: error.response?.data
      }
    };
  }
};

/**
 * Get current user's profile
 * @returns {Promise<Object>} Profile data
 */
authService.getProfile = async () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    const response = await apiGet('/api/auth/profile', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    // If successful and no data, fallback to localStorage
    if (response.success && !response.data) {
      const userString = localStorage.getItem('user');
      if (userString) {
        const userData = JSON.parse(userString);
        return {
          success: true,
          data: { user: userData }
        };
      }
    }

    return response;
  } catch (error) {
    console.error('Get Profile Error:', {
      message: error.message,
      stack: error.stack,
      type: error.type,
      code: error.code,
      name: error.name
    });

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to retrieve profile';

    return {
      success: false,
      message: errorMessage,
      error: error.message,
      details: {
        status: error.response?.status,
        data: error.response?.data
      }
    };
  }
};

/**
 * Update professional IDs
 * @param {Object} professionalData - Professional ID data to update
 * @returns {Promise<Object>} Update result
 */
authService.updateProfile = async (professionalData) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Use PUT method to match mobile app implementation
    const result = await apiPut('/api/auth/profile', professionalData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    // Comprehensive logging
    console.log('Profile Update Response:', {
      success: result.success,
      data: result.data,
      message: result.message
    });

    if (result.success) {
      // Update local storage user data
      const userString = localStorage.getItem('user');
      if (userString) {
        const userData = JSON.parse(userString);
        const updatedUserData = {
          ...userData,
          ...professionalData,
          aadhaar_number: professionalData.aadhaar_number || userData.aadhaar_number,
          pan_number: professionalData.pan_number || userData.pan_number,
          gst_number: professionalData.gst_number || userData.gst_number,
          tan_number: professionalData.tan_number || userData.tan_number
        };
        localStorage.setItem('user', JSON.stringify(updatedUserData));
      }
    }

    return {
      success: result.success,
      data: result.data,
      message: result.message || (result.success ? 'Profile updated successfully' : 'Failed to update profile')
    };
  } catch (error) {
    console.error('Profile Update Error:', {
      message: error.message,
      stack: error.stack,
      type: error.type,
      code: error.code,
      name: error.name,
      details: error.response
    });

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update profile';

    return {
      success: false,
      message: errorMessage,
      error: error.message,
      details: {
        status: error.response?.status,
        data: error.response?.data
      }
    };
  }
};

/**
 * Change user password
 * @param {Object} data - Password change data
 * @param {string} data.currentPassword - Current password
 * @param {string} data.newPassword - New password
 * @returns {Promise<Object>} - Change password response
 */
authService.changePassword = async (data) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    const response = await apiPost('/api/auth/change-password', {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.success,
      message: response.message || 'Password changed successfully'
    };
  } catch (error) {
    console.error('Change Password Error:', {
      message: error.message,
      stack: error.stack,
      type: error.type,
      code: error.code,
      name: error.name
    });

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to change password';

    return {
      success: false,
      message: errorMessage,
      error: error.message,
      details: {
        status: error.response?.status,
        data: error.response?.data
      }
    };
  }
};

/**
 * Logout user and clear authentication data
 * @returns {Promise<Object>} Logout result
 */
authService.logout = async () => {
  try {
    // Send logout request to backend
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    const response = await apiPost('/api/auth/logout', {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    // Clear local storage - including sub-user data (matching mobile app)
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('@rebax_subscription');
    localStorage.removeItem('isSubUser');
    localStorage.removeItem('actualRole');
    localStorage.removeItem('actualUserId');
    localStorage.removeItem('actualUserCity');
    localStorage.removeItem('parentUserId');
    localStorage.removeItem('actualUserName');

    return {
      success: true,
      message: response.message || 'Logged out successfully'
    };
  } catch (error) {
    console.error('Logout Error:', {
      message: error.message,
      stack: error.stack,
      type: error.type,
      code: error.code,
      name: error.name
    });

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to logout';

    return {
      success: false,
      message: errorMessage,
      error: error.message,
      details: {
        status: error.response?.status,
        data: error.response?.data
      }
    };
  }
};

/**
 * Send OTP to phone number or email
 * @param {string} phoneOrEmail - Phone number or email
 * @param {string} type - Type of OTP ('phone', 'email', 'registration', 'login', 'reset')
 * @returns {Promise<Object>} - OTP send response
 */
authService.sendOTP = async (phoneOrEmail, type = 'registration') => {
  // Backend currently expects 'phone' field for both email and phone
  // It validates based on format (email/phone) internally
  const response = await apiPost('/api/auth/send-otp', {
    phone: phoneOrEmail,
    type: type
  });
  return response;
};

/**
 * Verify OTP
 * @param {string} identifier - Email or phone number
 * @param {string} otp - OTP to verify
 * @param {string} type - 'email' or 'phone'
 * @returns {Promise<Object>} OTP verification result
 */
authService.verifyOTP = async (identifier, otp, type = 'email') => {
  try {
    const payload = {
      [type === 'email' ? 'email' : 'phone']: identifier,
      otp: otp,
      type: type
    };

    const response = await apiPost('/api/auth/verify-otp', payload);

    return {
      success: response.success,
      message: response.message || 'OTP verified successfully',
      data: response.data
    };
  } catch (error) {
    console.error('Verify OTP Error:', {
      message: error.message,
      stack: error.stack,
      type: error.type,
      code: error.code,
      name: error.name
    });

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to verify OTP';

    return {
      success: false,
      message: errorMessage,
      error: error.message,
      details: {
        status: error.response?.status,
        data: error.response?.data
      }
    };
  }
};

/**
 * Send Password Reset OTP
 * @param {Object} data - Email data for password reset
 * @returns {Promise<Object>} OTP send result
 */
authService.sendPasswordResetOTP = async (data) => {
  try {
    const payload = {
      email: data.email
    };

    const response = await apiPost('/api/auth/send-password-reset-otp', payload);

    return {
      success: response.success,
      message: response.message || 'OTP sent successfully',
      data: response.data
    };
  } catch (error) {
    console.error('Send Password Reset OTP Error:', {
      message: error.message,
      stack: error.stack,
      type: error.type,
      code: error.code,
      name: error.name
    });

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to send password reset OTP';

    return {
      success: false,
      message: errorMessage,
      error: error.message,
      details: {
        status: error.response?.status,
        data: error.response?.data
      }
    };
  }
};

/**
 * Verify Password Reset OTP
 * @param {Object} data - OTP verification data
 * @returns {Promise<Object>} OTP verification result
 */
authService.verifyPasswordResetOTP = async (data) => {
  try {
    const payload = {
      email: data.email,
      otp: data.otp
    };

    const response = await apiPost('/api/auth/verify-password-reset-otp', payload);

    return {
      success: response.success,
      message: response.message || 'OTP verified successfully',
      data: response.data
    };
  } catch (error) {
    console.error('Verify Password Reset OTP Error:', {
      message: error.message,
      stack: error.stack,
      type: error.type,
      code: error.code,
      name: error.name
    });

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to verify password reset OTP';

    return {
      success: false,
      message: errorMessage,
      error: error.message,
      details: {
        status: error.response?.status,
        data: error.response?.data
      }
    };
  }
};

/**
 * Send password reset OTP
 * @param {Object} data - Object containing email
 * @returns {Promise<Object>} - OTP send response
 */
authService.sendPasswordResetOTP = async (data) => {
  const response = await apiPost('/api/auth/forgot-password', {
    email: data.email
  });

  return response;
};

/**
 * Reset password using OTP
 * @param {Object} data - Reset password data
 * @returns {Promise<Object>} - Password reset response
 */
/**
 * Verify OTP
 * @param {string} phoneOrEmail - Phone number or email
 * @param {string} otp - OTP code
 * @param {string} type - Type of OTP
 * @returns {Promise<Object>} - Verification response
 */
authService.verifyOTP = async (phoneOrEmail, otp, type = 'reset') => {
  try {
    const response = await apiPost('/api/auth/verify-otp', {
      phone: phoneOrEmail,
      otp: otp,
      type: type
    });
    return response;
  } catch (error) {
    console.error('Verify OTP error:', error);
    return {
      success: false,
      message: error.message || 'Failed to verify OTP'
    };
  }
};

/**
 * Reset password using OTP
 * @param {Object} data - Reset password data
 * @param {string} data.email - User's email (will be used as phone param for backend)
 * @param {string} data.otp - OTP code
 * @param {string} data.newPassword - New password
 * @returns {Promise<Object>} - Success response
 */
authService.resetPasswordWithOTP = async (data) => {
  try {
    const response = await apiPost('/api/auth/reset-password', {
      phone: data.email, // Backend expects 'phone' field but accepts email
      otp: data.otp,
      newPassword: data.newPassword
    });

    return response;
  } catch (error) {
    console.error('Reset password error:', error);
    return {
      success: false,
      message: error.message || 'Failed to reset password'
    };
  }
};

/**
 * Verify PAN card
 * @param {string} pan - PAN number
 * @returns {Promise<Object>} - PAN verification response with name
 */
authService.verifyPAN = async (pan) => {
  try {
    const response = await apiPost('/api/auth/verify-pan', {
      pan: pan
    });
    return response;
  } catch (error) {
    console.error('Verify PAN error:', error);
    return {
      success: false,
      message: 'PAN verification failed'
    };
  }
};

/**
 * Verify OTP for email or phone
 * @param {Object} otpData - OTP verification data
 * @param {string} otpData.type - 'email' or 'phone'
 * @param {string} otpData.otp - OTP entered by user
 * @param {string} [otpData.email] - Email for verification (if type is 'email')
 * @param {string} [otpData.phone] - Phone number for verification (if type is 'phone')
 * @returns {Promise<Object>} OTP verification response
 */
/**
 * Verify OTP - Matching mobile app implementation exactly
 * @param {string} phoneOrEmail - Phone number or email
 * @param {string} otp - OTP code
 * @param {string} type - Type of OTP ('email', 'phone', 'registration')
 * @returns {Promise<Object>} OTP verification response
 */
authService.verifyOTP = async (phoneOrEmail, otp, type = 'registration') => {
  try {
    console.log('🔍 VerifyOTP called with:', { phoneOrEmail, otp, type });

    // Backend expects 'phone' field for both email and phone (same as mobile app)
    // It validates based on format (email/phone) internally
    const payload = {
      phone: phoneOrEmail,
      otp: otp,
      type: type
    };

    console.log('📤 VerifyOTP API payload:', payload);
    const response = await apiPost('/api/auth/verify-otp', payload);
    console.log('📥 VerifyOTP API response:', response);

    return response;
  } catch (error) {
    console.error('❌ Verify OTP error:', error);
    return {
      success: false,
      message: error.message || 'Failed to verify OTP'
    };
  }
};

/**
 * Register new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} - Registration response
 */
authService.register = async (userData) => {
  try {
    const response = await apiPost('/api/auth/register', userData);

    // If registration is successful, optionally handle token or user data
    if (response.success && response.data && response.data.token) {
      // Store token in localStorage
      localStorage.setItem('authToken', response.data.token);

      // Store user data
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      // Store refresh token if available
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
    }

    return response;
  } catch (error) {
    console.error('Registration error:', error);
    return {
      success: false,
      message: error.message || 'Registration failed. Please try again.'
    };
  }
};

/**
 * Register agent user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} - Registration response
 */
authService.agentRegister = async (userData) => {
  try {
    const registrationData = {
      ...userData,
      role: 'agent'
    };

    const response = await apiPost('/api/auth/register', registrationData);

    // If registration is successful, store token and user data
    if (response.success && response.data && response.data.token) {
      // Store authentication token
      localStorage.setItem('authToken', response.data.token);

      // Store user data
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      // Store refresh token if available
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
    }

    return response;
  } catch (error) {
    console.error('Agent Registration error:', error);
    return {
      success: false,
      message: error.message || 'Agent registration failed. Please try again.'
    };
  }
};

/**
 * Attempt to refresh the authentication token
 * @returns {Promise<Object>} Token refresh result
 */
authService.refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    const currentUser = JSON.parse(localStorage.getItem('user'));

    if (!refreshToken || !currentUser) {
      console.warn('🚫 No refresh token or user found');
      return { success: false, message: 'No refresh token available' };
    }

    console.group('🔄 Token Refresh Attempt');
    console.log('Attempting to refresh token for user:', currentUser.email);

    const response = await apiPost('/api/auth/refresh-token', {
      email: currentUser.email,
      refreshToken
    });

    console.log('Token Refresh Response:', response);
    console.groupEnd();

    if (response.success && response.data?.token) {
      // Update stored tokens
      localStorage.setItem('authToken', response.data.token);
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }

      return {
        success: true,
        token: response.data.token,
        message: 'Token refreshed successfully'
      };
    } else {
      // If refresh fails, logout
      await this.logout();
      return {
        success: false,
        message: response.message || 'Token refresh failed'
      };
    }
  } catch (error) {
    console.error('🚨 Token Refresh Error:', error);
    await this.logout();
    return {
      success: false,
      message: error.message || 'Failed to refresh token'
    };
  }
};

/**
 * Get users list with filtering and pagination (matching mobile app adminCrmService.getUsers)
 * @param {Object} filters - Filter options
 * @returns {Promise<Object>} Users list response
 */
authService.getUsers = async (filters = {}) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Build query params exactly like mobile app
    const queryParams = new URLSearchParams();
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.role) queryParams.append('role', filters.role);
    if (filters.subscription_status) queryParams.append('subscription_status', filters.subscription_status);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);

    // Use exact endpoint from mobile app (no manual query string building)
    const endpoint = '/api/admin/crm-users';

    // Convert URLSearchParams to plain object for apiGet
    const params = {};
    queryParams.forEach((value, key) => {
      params[key] = value;
    });

    const response = await apiGet(endpoint, params, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Get Users Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch users'
    };
  }
};

// Note: Subscription plans are now loaded using billingService.getPublicSubscriptionPlansWithFilters
// exactly like in the mobile app to ensure consistency

/**
 * Assign subscription plan to user (matching mobile app adminCrmService.assignPlan)
 * @param {Object} data - Assignment data with user_id, plan_id, and days/months
 * @returns {Promise<Object>} Assignment response
 */
authService.assignSubscriptionPlan = async (data) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    const { user_id, plan_id, months } = data;
    // Convert months to days if needed (mobile app uses days)
    const days = months ? months * 30 : data.days;

    // Use exact endpoint and payload from mobile app
    const response = await apiPost(`/api/admin/crm-users/${user_id}/assign-plan`, {
      plan_id: plan_id,
      days: days
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Assign Subscription Plan Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to assign subscription plan'
    };
  }
};

/**
 * Assign credits to user (matching mobile app adminCrmService.assignCredits)
 * @param {Object} data - Credits assignment data
 * @returns {Promise<Object>} Assignment response
 */
authService.assignCredits = async (data) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Use exact endpoint from mobile app
    const response = await apiPost('/api/admin/credits/assign', {
      user_id: data.user_id,
      credits: data.credits,
      description: data.description || 'Admin CRM assignment'
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Assign Credits Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to assign credits'
    };
  }
};

/**
 * Get a builder's credit balance (matching mobile app adminCrmService.getBuilderBalance)
 * @param {string} userId - Builder user ID
 * @returns {Promise<Object>} Balance response
 */
authService.getBuilderBalance = async (userId) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    const response = await apiGet(`/api/admin/credits/balances/${userId}`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Get Builder Balance Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch credit balance'
    };
  }
};

/**
 * Add sub-user to parent user (matching mobile app adminCrmService.createSubUser)
 * @param {Object} data - Sub-user data with parent_id
 * @returns {Promise<Object>} Sub-user creation response
 */
authService.addSubUser = async (data) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    const { parent_id, ...subUserData } = data;

    // Use exact endpoint from mobile app
    const response = await apiPost(`/api/admin/crm-users/${parent_id}/sub-users`, subUserData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Add Sub-User Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to add sub-user'
    };
  }
};

/**
 * Delete sub-user (matching mobile app adminCrmService.deleteSubUser)
 * @param {Object} data - User deletion data
 * @returns {Promise<Object>} Deletion response
 */
authService.deleteSubUser = async (data) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    const { parent_id, sub_user_id } = data;

    // Use exact endpoint from mobile app
    const response = await apiPost(`/api/admin/crm-users/${parent_id}/sub-users/${sub_user_id}`, {}, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Delete Sub-User Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to delete sub-user'
    };
  }
};

/**
 * Get sub-users for a parent user (matching mobile app adminCrmService.getSubUsers)
 * @param {string} userId - Parent user ID
 * @returns {Promise<Object>} Sub-users response
 */
authService.getSubUsers = async (userId) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Use exact endpoint from mobile app
    const response = await apiGet(`/api/admin/crm-users/${userId}/sub-users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Get Sub-Users Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch sub-users'
    };
  }
};

/**
 * Get sub-user limits for a parent user (matching mobile app adminCrmService.getSubUserLimits)
 * @param {string} userId - Parent user ID
 * @returns {Promise<Object>} Sub-user limits response
 */
authService.getSubUserLimits = async (userId) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return { success: false, message: 'No authentication token found' };
    }
    const response = await apiGet(`/api/admin/crm-users/${userId}/sub-user-limits`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response;
  } catch (error) {
    console.error('Get Sub-User Limits Error:', error);
    return { success: false, message: error.message || 'Failed to fetch sub-user limits' };
  }
};

/**
 * Register new user via admin CRM (matching mobile app adminCrmService.registerUser)
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} Registration response
 */
authService.registerCrmUser = async (userData) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Use exact endpoint from mobile app
    const response = await apiPost('/api/admin/crm-users/register', userData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Register CRM User Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to register user'
    };
  }
};

/**
 * Get subscription plans for a role (matching mobile app)
 * @param {string} role - User role to get plans for
 * @returns {Promise<Object>} Plans response
 */
authService.getSubscriptionPlans = async (role) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Use exact endpoint from mobile app
    const response = await apiGet(`/api/admin/subscription-plans?role=${role}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Get Subscription Plans Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch subscription plans'
    };
  }
};

/**
 * Assign subscription plan (matching mobile app adminCrmService.assignPlan)
 * @param {Object} data - Plan assignment data {user_id, plan_id, days}
 * @returns {Promise<Object>} Assignment response
 */
authService.assignSubscriptionPlan = async (data) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Use exact endpoint from mobile app: /api/admin/crm-users/${userId}/assign-plan
    const response = await apiPost(`/api/admin/crm-users/${data.user_id}/assign-plan`, {
      plan_id: data.plan_id,
      days: data.days
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Assign Subscription Plan Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to assign subscription plan'
    };
  }
};

/**
 * Extend subscription (matching mobile app adminCrmService.extendSubscription)
 * @param {Object} data - Extension data {user_id, days}
 * @returns {Promise<Object>} Extension response
 */
authService.extendSubscription = async (data) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: 'No authentication token found'
      };
    }

    // Use exact endpoint from mobile app: /api/admin/crm-users/${userId}/extend-subscription
    const response = await apiPost(`/api/admin/crm-users/${data.user_id}/extend-subscription`, {
      days: data.days
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response;
  } catch (error) {
    console.error('Extend Subscription Error:', error);
    return {
      success: false,
      message: error.message || 'Failed to extend subscription'
    };
  }
};

export default authService;